import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppContext } from '../context/AppContext';
import { 
  subscribeToUserNotifications, 
  markNotificationAsRead,
  markAllNotificationsAsRead,
  deleteAllNotifications 
} from '../services/notificationService';
import {
  getUserPendingInvitations,
  acceptInvitation,
  rejectInvitation
} from '../services/invitationService';
import Button from '../components/ui/Button';
import Modal from '../components/ui/Modal';
import { icons } from '../components/ui/Icons';
import toast from 'react-hot-toast';

const NotificationsPage = () => {
  const { currentUser } = useAppContext();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [processingInvitation, setProcessingInvitation] = useState(null);

  useEffect(() => {
    if (!currentUser) {
      navigate('/login');
      return;
    }

    const unsubscribe = subscribeToUserNotifications(currentUser.id, (notifs) => {
      setNotifications(notifs);
    });

    // Carregar convites pendentes
    loadPendingInvitations();

    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, navigate]);

  const loadPendingInvitations = async () => {
    if (!currentUser) return;
    
    try {
      const pendingInvitations = await getUserPendingInvitations(currentUser.id);
      setInvitations(pendingInvitations);
    } catch (error) {
      console.error('Erro ao carregar convites:', error);
    }
  };

  const handleAcceptInvitation = async (invitationId) => {
    setProcessingInvitation(invitationId);
    try {
      await acceptInvitation(invitationId, currentUser.id);
      toast.success('Convite aceito! Agora você é um profissional desta barbearia.');
      loadPendingInvitations();
    } catch (error) {
      console.error('Erro ao aceitar convite:', error);
      toast.error(error.message || 'Erro ao aceitar convite');
    } finally {
      setProcessingInvitation(null);
    }
  };

  const handleRejectInvitation = async (invitationId) => {
    setProcessingInvitation(invitationId);
    try {
      await rejectInvitation(invitationId, currentUser.id);
      toast.success('Convite recusado');
      loadPendingInvitations();
    } catch (error) {
      console.error('Erro ao recusar convite:', error);
      toast.error(error.message || 'Erro ao recusar convite');
    } finally {
      setProcessingInvitation(null);
    }
  };

  const handleNotificationClick = async (notification) => {
    if (!notification.read) {
      await markNotificationAsRead(notification.id);
    }

    // Se for notificação de convite, não redirecionar (os botões estão na seção de convites pendentes)
    if (notification.type === 'barbershop_invitation') {
      // Apenas marcar como lida, usuário pode ver os botões na seção "Convites Pendentes"
      return;
    }

    // Para outros tipos de notificação, redirecionar para a barbearia
    if (notification.data?.barbershopId) {
      navigate(`/barbershop/${notification.data.barbershopId}`);
    }
  };

  const handleMarkAllAsRead = async () => {
    await markAllNotificationsAsRead(currentUser.id);
    toast.success('Todas as notificações foram marcadas como lidas');
  };

  const handleDeleteAll = async () => {
    setLoading(true);
    try {
      await deleteAllNotifications(currentUser.id);
      toast.success('Todas as notificações foram apagadas');
      setShowDeleteModal(false);
    } catch (error) {
      toast.error('Erro ao apagar notificações');
      console.error('Error deleting notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'appointment_created':
        return '🔔';
      case 'appointment_confirmed':
        return '✅';
      case 'appointment_cancelled':
        return '❌';
      case 'appointment_reminder':
        return '⏰';
      case 'barbershop_invitation':
        return '💼';
      case 'invitation_accepted':
        return '🎉';
      case 'invitation_rejected':
        return '🚫';
      case 'removed_from_barbershop':
        return '👋';
      default:
        return '📢';
    }
  };

  const formatTimeAgo = (timestamp) => {
    // Lidar com diferentes tipos de data (Timestamp do Firestore ou string)
    let date;
    if (timestamp?.toDate) {
      // É um Timestamp do Firestore
      date = timestamp.toDate();
    } else if (timestamp) {
      // É uma string ou já é um Date
      date = new Date(timestamp);
    } else {
      return 'Data inválida';
    }

    // Validar se a data é válida
    if (isNaN(date.getTime())) {
      return 'Data inválida';
    }

    const now = new Date();
    const diffInMs = now - date;
    const diffInMinutes = Math.floor(diffInMs / 60000);
    const diffInHours = Math.floor(diffInMinutes / 60);
    const diffInDays = Math.floor(diffInHours / 24);

    if (diffInMinutes < 1) return 'Agora mesmo';
    if (diffInMinutes < 60) return `${diffInMinutes} min atrás`;
    if (diffInHours < 24) return `${diffInHours}h atrás`;
    if (diffInDays < 7) return `${diffInDays}d atrás`;
    return date.toLocaleDateString('pt-BR');
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <div className="bg-gray-50 min-h-screen">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Notificações</h1>
              <p className="text-sm text-gray-600 mt-1">
                {notifications.length === 0 
                  ? 'Você não tem notificações' 
                  : `${notifications.length} notificação${notifications.length > 1 ? 'ões' : ''} ${unreadCount > 0 ? `(${unreadCount} não lida${unreadCount > 1 ? 's' : ''})` : ''}`
                }
              </p>
            </div>

            {/* Ações */}
            {notifications.length > 0 && (
              <div className="flex flex-col sm:flex-row gap-2">
                {unreadCount > 0 && (
                  <Button
                    variant="secondary"
                    onClick={handleMarkAllAsRead}
                    className="text-sm whitespace-nowrap"
                  >
                    {icons.checkCircle({ className: 'w-4 h-4 mr-2' })}
                    Marcar todas como lidas
                  </Button>
                )}
                <Button
                  variant="danger"
                  onClick={() => setShowDeleteModal(true)}
                  className="text-sm whitespace-nowrap"
                >
                  {icons.trash({ className: 'w-4 h-4 mr-2' })}
                  Apagar todas
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Convites Pendentes */}
        {invitations.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
              <span className="text-2xl mr-2">💼</span>
              Convites para Profissional
            </h2>
            <div className="space-y-4">
              {invitations.map((invitation) => (
                <div
                  key={invitation.id}
                  className="bg-gradient-to-r from-indigo-50 to-purple-50 border-2 border-indigo-200 rounded-lg p-4"
                >
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900 mb-1">
                        {invitation.barbershopName}
                      </h3>
                      <p className="text-sm text-gray-700 mb-2">
                        <strong>{invitation.invitedByName}</strong> convidou você para ser profissional desta barbearia
                      </p>
                      <div className="flex flex-wrap gap-2 text-xs text-gray-600">
                        <span className="flex items-center">
                          <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                          </svg>
                          Recebido em {invitation.createdAt?.toDate().toLocaleDateString('pt-BR')}
                        </span>
                        <span className="flex items-center">
                          <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                          </svg>
                          Expira em {invitation.expiresAt?.toDate().toLocaleDateString('pt-BR')}
                        </span>
                      </div>
                    </div>
                    <div className="flex gap-2 sm:flex-col">
                      <Button
                        onClick={() => handleAcceptInvitation(invitation.id)}
                        disabled={processingInvitation === invitation.id}
                        className="flex-1 sm:flex-none bg-green-600 hover:bg-green-700"
                      >
                        {processingInvitation === invitation.id ? (
                          <span className="flex items-center justify-center">
                            <svg className="animate-spin h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Processando...
                          </span>
                        ) : (
                          <>
                            {icons.checkCircle({ className: 'w-4 h-4 mr-2' })}
                            Aceitar
                          </>
                        )}
                      </Button>
                      <Button
                        onClick={() => handleRejectInvitation(invitation.id)}
                        disabled={processingInvitation === invitation.id}
                        variant="secondary"
                        className="flex-1 sm:flex-none"
                      >
                        {icons.xCircle({ className: 'w-4 h-4 mr-2' })}
                        Recusar
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-blue-800">
                <strong>💡 Dica:</strong> Ao aceitar, você se tornará co-proprietário da barbearia e poderá gerenciá-la. 
                Não esqueça de adicionar sua especialidade e experiência no seu perfil!
              </p>
            </div>
          </div>
        )}

        {/* Lista de Notificações */}
        {notifications.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <div className="text-6xl mb-4">🔕</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Nenhuma notificação
            </h3>
            <p className="text-gray-600">
              Você será notificado aqui sobre novidades e atualizações
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {notifications.map((notification) => (
              <button
                key={notification.id}
                onClick={() => handleNotificationClick(notification)}
                className={`w-full bg-white rounded-lg shadow-sm p-4 sm:p-6 text-left hover:shadow-md transition-all ${
                  !notification.read ? 'ring-2 ring-indigo-500 bg-indigo-50' : ''
                }`}
              >
                <div className="flex items-start gap-3 sm:gap-4">
                  <span className="text-3xl sm:text-4xl flex-shrink-0">
                    {getNotificationIcon(notification.type)}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <h3 className={`text-base sm:text-lg font-medium text-gray-900 ${
                        !notification.read ? 'font-bold' : ''
                      }`}>
                        {notification.title}
                      </h3>
                      {!notification.read && (
                        <span className="w-3 h-3 bg-indigo-600 rounded-full flex-shrink-0 mt-1"></span>
                      )}
                    </div>
                    <p className="text-sm sm:text-base text-gray-600 mt-1">
                      {notification.message}
                    </p>
                    <p className="text-xs sm:text-sm text-gray-400 mt-2">
                      {formatTimeAgo(notification.createdAt)}
                    </p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}

        {/* Modal de Confirmação */}
        <Modal
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          title="Apagar todas as notificações?"
        >
          <div className="space-y-4">
            <p className="text-gray-600">
              Tem certeza que deseja apagar todas as notificações? Esta ação não pode ser desfeita.
            </p>
            <div className="flex flex-col-reverse sm:flex-row gap-3 sm:justify-end">
              <Button
                variant="secondary"
                onClick={() => setShowDeleteModal(false)}
                disabled={loading}
                className="w-full sm:w-auto"
              >
                Cancelar
              </Button>
              <Button
                variant="danger"
                onClick={handleDeleteAll}
                disabled={loading}
                className="w-full sm:w-auto"
              >
                {loading ? 'Apagando...' : 'Sim, apagar tudo'}
              </Button>
            </div>
          </div>
        </Modal>
      </div>
    </div>
  );
};

export default NotificationsPage;
